import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Trophy, Eye, EyeOff } from "lucide-react";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "player", access_code: "", code: "", newPassword: "", membership_type: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    api.get("/registration-status").then((r) => setRegOpen(r.data.open)).catch(() => {});
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setNotice("");
    setCodeSent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/");
      } else if (mode === "register") {
        if ((form.role === "player" || form.role === "parent") && !form.membership_type) {
          setError("Veuillez indiquer si vous souhaitez faire un essai ou vous affilier.");
          setLoading(false);
          return;
        }
        await register(form);
        navigate("/");
      } else if (mode === "forgot") {
        if (!codeSent) {
          const r = await api.post("/auth/forgot-password", { email: form.email });
          setCodeSent(true);
          setNotice(r.data?.message || "Si un compte existe, un code a été envoyé par email.");
        } else {
          const r = await api.post("/auth/reset-password", {
            email: form.email, code: form.code, new_password: form.newPassword,
          });
          switchMode("login");
          setNotice(r.data?.message || "Mot de passe réinitialisé. Vous pouvez vous connecter.");
          setForm({ ...form, password: "", code: "", newPassword: "" });
        }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: { h: "Connexion", p: "Accédez à l'espace de votre club." },
    register: { h: "Créer un compte", p: "Rejoignez la plateforme du club." },
    forgot: { h: "Mot de passe oublié", p: "Recevez un code par email pour réinitialiser votre mot de passe." },
  };
  const submitLabel = loading ? "..."
    : mode === "login" ? "Se connecter"
    : mode === "register" ? "S'inscrire"
    : !codeSent ? "Envoyer le code" : "Réinitialiser le mot de passe";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundImage: "linear-gradient(to bottom, rgba(5,7,9,0.55), rgba(5,7,9,0.92)), url('https://images.pexels.com/photos/15964698/pexels-photo-15964698.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="flex items-center gap-3">
          <Trophy className="text-primary" size={32} />
          <span className="font-display text-2xl font-extrabold uppercase tracking-tighter">FKA Quaregnon</span>
        </div>
        <div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter leading-none">
            À Quaregnon<br />le futsal<br /><span className="text-primary">c'est fun.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
            Restez connectés, vivez la saison au plus près.
          </p>
        </div>
        <div className="flex gap-8 text-sm uppercase tracking-widest text-slate-400 font-bold">
          <span>Catégories</span><span>Présence</span><span>Compétences</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Trophy className="text-primary" size={26} />
            <span className="font-display text-xl font-extrabold uppercase">FKA Quaregnon</span>
          </div>
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight">{titles[mode].h}</h2>
          <p className="text-muted-foreground text-sm mt-2 mb-8">{titles[mode].p}</p>

          {error && (
            <div data-testid="auth-error" className="mb-4 px-4 py-3 rounded-lg bg-destructive/15 border border-destructive/40 text-destructive text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div data-testid="auth-notice" className="mb-4 px-4 py-3 rounded-lg bg-primary/15 border border-primary/40 text-primary text-sm">
              {notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Nom complet">
                <input data-testid="name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input" placeholder="Karim Benali" />
              </Field>
            )}

            <Field label="Email">
              <input data-testid="email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input" placeholder="vous@exemple.com" disabled={mode === "forgot" && codeSent} />
            </Field>

            {(mode === "login" || mode === "register") && (
              <Field label="Mot de passe">
                <div className="relative">
                  <input data-testid="password-input" type={showPassword ? "text" : "password"} required value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="input" style={{ paddingRight: 44 }} placeholder="••••••••" />
                  <button type="button" data-testid="toggle-password-visibility"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </Field>
            )}

            {mode === "forgot" && codeSent && (
              <>
                <Field label="Code reçu par email">
                  <input data-testid="reset-code-input" required value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    className="input" placeholder="123456" inputMode="numeric" style={{ letterSpacing: "6px", fontWeight: 700 }} />
                </Field>
                <Field label="Nouveau mot de passe">
                  <div className="relative">
                    <input data-testid="new-password-input" type={showNewPassword ? "text" : "password"} required value={form.newPassword}
                      onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                      className="input" style={{ paddingRight: 44 }} placeholder="Min. 6 caractères" />
                    <button type="button" data-testid="toggle-new-password-visibility"
                      aria-label={showNewPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors">
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </Field>
              </>
            )}

            {mode === "register" && (
              <Field label="Vous êtes">
                <select data-testid="role-select" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
                  <option value="player">Joueur</option>
                  <option value="parent">Parent</option>
                  <option value="coach">Coach</option>
                  <option value="externe">Externe</option>
                </select>
              </Field>
            )}
            {mode === "register" && (form.role === "player" || form.role === "parent") && (
              <Field label="Vous souhaitez :">
                <div className="grid grid-cols-1 gap-2" data-testid="membership-choice">
                  <button type="button" data-testid="membership-essai-btn"
                    onClick={() => setForm({ ...form, membership_type: "essai" })}
                    className={`py-2.5 px-3 rounded-xl border text-left transition-colors ${form.membership_type === "essai" ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-primary/40"}`}>
                    <span className="block text-sm font-bold">Faire un essai</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">Séance découverte, sans engagement</span>
                  </button>
                  <button type="button" data-testid="membership-stages-btn"
                    onClick={() => setForm({ ...form, membership_type: "stages" })}
                    className={`py-2.5 px-3 rounded-xl border text-left transition-colors ${form.membership_type === "stages" ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-primary/40"}`}>
                    <span className="block text-sm font-bold">Participer aux stages</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">Stages pendant les vacances</span>
                  </button>
                  <button type="button" data-testid="membership-affilie-btn"
                    onClick={() => setForm({ ...form, membership_type: "affilie" })}
                    className={`py-2.5 px-3 rounded-xl border text-left transition-colors ${form.membership_type === "affilie" ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-primary/40"}`}>
                    <span className="block text-sm font-bold">M'affilier</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">Inscription pour la saison</span>
                  </button>
                </div>
              </Field>
            )}
            {mode === "register" && (
              <Field label={regOpen ? "Code d'accès (optionnel)" : "Code d'accès"}>
                <input data-testid="access-code-input" value={form.access_code}
                  onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase() })}
                  className="input" placeholder="Ex. A1B2C3D4" style={{ letterSpacing: "2px" }} />
                {!regOpen && (
                  <p data-testid="registration-closed-note" className="text-xs text-muted-foreground mt-2">
                    Les inscriptions libres sont fermées. Un code d'accès fourni par le club est requis.
                  </p>
                )}
              </Field>
            )}

            <button data-testid="auth-submit-btn" disabled={loading}
              className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-bold uppercase tracking-wide text-sm hover:bg-primary/90 transition-colors disabled:opacity-60">
              {submitLabel}
            </button>
          </form>

          {mode !== "forgot" && (
            <p className="mt-6 text-sm text-muted-foreground">
              {mode === "login" ? "Pas encore de compte ? " : "Déjà inscrit ? "}
              <button data-testid="toggle-auth-mode" onClick={() => switchMode(mode === "login" ? "register" : "login")}
                className="text-primary font-bold hover:underline">
                {mode === "login" ? "Créer un compte" : "Se connecter"}
              </button>
            </p>
          )}
          {mode === "login" && (
            <p className="mt-3 text-sm text-muted-foreground">
              <button data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
                className="text-primary font-bold hover:underline">
                Mot de passe oublié ?
              </button>
            </p>
          )}
          {mode === "forgot" && (
            <p className="mt-6 text-sm text-muted-foreground">
              <button data-testid="back-to-login" onClick={() => switchMode("login")}
                className="text-primary font-bold hover:underline">
                ← Retour à la connexion
              </button>
            </p>
          )}
        </div>
      </div>

      <style>{`.input{width:100%;height:48px;padding:0 16px;border-radius:10px;background:hsl(var(--secondary));border:1px solid hsl(var(--border));color:hsl(var(--foreground));font-size:14px;outline:none}.input:focus{border-color:hsl(var(--primary));box-shadow:0 0 0 2px hsl(var(--primary)/.3)}.input:disabled{opacity:.6}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{label}</label>
    {children}
  </div>
);
